import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSTitleCard } from '@/components/shared/card/Card';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { useEmployeeWorkingPatternQuery, useGetMeQuery, WorkingPatternDay } from '@/services/gql/graphql.generated';
import strings from '../../ess.translations';

import { dateToApiFormat, secondsToTimeString } from '@/helpers/dateHelper';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { FunctionComponent, Suspense } from 'react';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { MyScheduleHeader } from './MyScheduleHeader';
import MyRequestAlertCard from './MyWorkingPatternRequestUpdateCard';
import { getOrderedSchedule } from './getOrderedSchedule';
import { MyScheduleUpcomingLeaveCard } from './leave/MyScheduleUpcomingLeaveCard';
import { useScheduleAlerts } from './useScheduleAlerts';

const MySchedule: FunctionComponent = () => {
  const navigate = useNavigate();
  const [{ data: meData }] = useGetMeQuery();
  const allowEssWorkingPatterns = useSystemSettings(state => state.settings.allowEssWorkingPatterns);

  const [{ data: myWorkingPatternData }] = useEmployeeWorkingPatternQuery({
    variables: {
      date: dateToApiFormat(new Date())
    },
    requestPolicy: 'network-only'
  });

  if (
    !myWorkingPatternData?.myWorkingPatterns?.periods ||
    myWorkingPatternData?.myWorkingPatterns?.periods.length === 0
  ) {
    throw Error('Employee working pattern data should be setup');
  }

  const patterns = myWorkingPatternData?.myWorkingPatterns?.periods[0].patterns as WorkingPatternDay[];
  if (!patterns) {
    throw Error('Patterns should be defined.');
  }

  const employeeId = meData?.myEmployee?.identityId;
  const { latestAlert, hasPendingRequest } = useScheduleAlerts(employeeId);

  const orderedSchedule = getOrderedSchedule(patterns);

  const onEditClick = () => {
    navigate(`edit`);
  };

  return (
    <PageContainer customHeader={<MyScheduleHeader />}>
      <Suspense fallback={<TableLoader />}>
        <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto space-y-4">
          {latestAlert && (
            <MyRequestAlertCard
              key={latestAlert.key}
              message={latestAlert.message}
              status={latestAlert.status}
              onView={latestAlert.onView}
              onDismiss={latestAlert.onDismiss}
            />
          )}

          <TSTitleCard
            primaryAction={
              hasPendingRequest || !allowEssWorkingPatterns
                ? undefined
                : {
                    label: strings.mySchedule.editLabel,
                    onClick: onEditClick
                  }
            }
            title={strings.mySchedule.cardTitle}
            icon={AiOutlineCalendar}
          >
            <div className="grid grid-flow-row grid-cols-1 sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-4 gap-y-5 gap-x-8 lg:max-w-6xl lg:mx-auto lg:px-8">
              {orderedSchedule.map(item => (
                <div
                  key={item.dayNumber}
                  className="grid grid-cols-2 gap-x-6 items-start"
                  style={{ gridTemplateColumns: '0.8fr 1.2fr' }}
                >
                  <p className="text-right font-semibold sm:px-0">{item.day}</p>
                  <p className="break-keep">
                    {item.periods.length > 0
                      ? item.periods
                          .map(
                            period =>
                              `${secondsToTimeString(period.startTime!)} - ${secondsToTimeString(period.endTime!)}`
                          )
                          .join(', ')
                      : strings.mySchedule.notAvailable}
                  </p>
                </div>
              ))}
            </div>
          </TSTitleCard>
          <MyScheduleUpcomingLeaveCard userId={employeeId} />
        </PageSizer>
      </Suspense>
    </PageContainer>
  );
};

export default MySchedule;
